import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { IS_PRODUCTION_ORIGIN } from '~/constants/config/env';

import type {
  HealthAllResponse,
  ServiceHealth,
  ServiceHealthResponse,
} from '../types/health.types';

/** Fetch aggregate backend health from the gateway's `/health/all` fan-out
 *  (the core wake-up services). Used by the login warm-up. */
export async function getServiceHealth(): Promise<HealthAllResponse> {
  return integrationsClient.get(endpoints.serviceHealth.all()).json<HealthAllResponse>();
}

/** Probe/wake a single service by id (platform service or agent name). Each
 *  Health-page card calls this independently so services load — and cold-start
 *  — on their own. */
export async function getServiceById(id: string): Promise<ServiceHealth> {
  const res = await integrationsClient
    .get(endpoints.serviceHealth.service(id))
    .json<ServiceHealthResponse>();
  return res.service;
}

/** Fire the health/wake fan-out without waiting on or surfacing the result.
 *  Used on login to warm the core services (min-instances=0 → 1) so the first
 *  real user action doesn't pay the cold-start cost. Best-effort: any failure is
 *  swallowed so it can never block or error the login flow.
 *
 *  Production-only: min-instances=0 (and thus the cold-start cost this avoids)
 *  is a production configuration, and we don't want every dev/staging login
 *  firing a fan-out at those environments' backends. No-op elsewhere.
 *
 *  Gated on `IS_PRODUCTION_ORIGIN` (production build AND production host) rather than
 *  the build flag alone, because a bare `vite build` defaults to mode `production` — a
 *  mis-wired non-prod workflow would otherwise aim this at a non-prod backend. */
export function wakeServices(): void {
  if (!IS_PRODUCTION_ORIGIN) return;
  void getServiceHealth().catch(() => {
    // Intentionally ignored — waking is opportunistic, not required for login.
  });
}
